/**
 * DataCleaningConfig Component
 *
 * Inline configuration component for data cleaning options.
 * Rendered inside CategoryEditView when the data cleaning config is expanded.
 *
 * @param {Object} props
 * @param {Object} props.config - Current configuration values
 * @param {Function} props.onChange - Called with new config on any change
 */

import React, { useState, useEffect } from "react";
import { FaFilter, FaCog } from "react-icons/fa";
import { IoClose } from "react-icons/io5";

const DATA_CLEANING_ONBOARDING_KEY = "cloudDataCleaningOnboardingDismissed";

// Toggle switch component (matching PageUnblockerConfig style)
const Toggle = ({ enabled, onChange, disabled = false }) => (
  <button
    type="button"
    onClick={() => !disabled && onChange(!enabled)}
    disabled={disabled}
    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
      disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
    } ${enabled ? "bg-emerald-500" : "bg-gray-300 dark:bg-gray-600"}`}
  >
    <span
      className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
        enabled ? "translate-x-5" : "translate-x-1"
      }`}
    />
  </button>
);

const DataCleaningConfig = ({ config, onChange }) => {
  const [showOnboarding, setShowOnboarding] = useState(false);

  useEffect(() => {
    try {
      if (!localStorage.getItem(DATA_CLEANING_ONBOARDING_KEY)) {
        setShowOnboarding(true);
      }
    } catch (err) {
      console.error("Failed to check data cleaning onboarding state:", err);
    }
  }, []);

  const dismissOnboarding = () => {
    setShowOnboarding(false);
    try {
      localStorage.setItem(DATA_CLEANING_ONBOARDING_KEY, "true");
    } catch (err) {
      console.error("Failed to save data cleaning onboarding dismissal:", err);
    }
  };

  const handleToggle = (field) => {
    onChange({ ...config, [field]: !config[field] });
  };

  const handleThresholdChange = (field, value) => {
    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue >= 0 && numValue <= 1) {
      onChange({ ...config, [field]: numValue });
    }
  };

  return (
    <div className="space-y-2.5">
      {/* Onboarding Illustration */}
      {showOnboarding && (
        <div className="relative rounded-xl overflow-hidden border border-emerald-200 dark:border-emerald-500/20 bg-gradient-to-br from-white dark:from-gray-900/80 via-gray-50 dark:via-gray-900/70 to-emerald-50 dark:to-emerald-950/40 backdrop-blur-md shadow-xl">
          {/* Close button */}
          <button
            onClick={dismissOnboarding}
            className="absolute top-2 right-2 z-10 w-6 h-6 rounded-lg flex items-center justify-center
                       bg-gray-100 dark:bg-gray-800/60 border border-gray-300 dark:border-gray-600/40 hover:bg-gray-200 dark:hover:bg-gray-700/80 hover:border-gray-400 dark:hover:border-gray-500/50
                       transition-all duration-200"
          >
            <IoClose className="w-3.5 h-3.5 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200" />
          </button>

          <div className="px-4 pt-4 pb-3">
            {/* Illustration */}
            <div className="flex justify-center mb-3">
              <div className="relative">
                <div className="absolute inset-0 bg-emerald-500/10 rounded-full blur-2xl scale-150" />
                {/* Mini spreadsheet */}
                <div className="relative w-52 h-36 rounded-xl bg-white dark:bg-gray-900/80 border border-gray-300 dark:border-gray-600/40 shadow-xl overflow-hidden flex flex-col">
                  {/* Header row */}
                  <div className="flex items-center gap-px px-1.5 py-1.5 bg-gray-100 dark:bg-gray-800/90 border-b border-gray-200 dark:border-gray-700/50">
                    <div className="flex-1 h-2 bg-emerald-200 dark:bg-emerald-500/25 rounded-sm border border-emerald-300 dark:border-emerald-500/20" />
                    <div className="flex-1 h-2 bg-emerald-200 dark:bg-emerald-500/25 rounded-sm border border-emerald-300 dark:border-emerald-500/20 mx-0.5" />
                    <div className="flex-1 h-2 bg-red-100 dark:bg-red-400/15 rounded-sm border border-red-200 dark:border-red-400/20 relative">
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-full h-px bg-red-400/40 rotate-[-8deg]" />
                      </div>
                    </div>
                    <div className="flex-1 h-2 bg-emerald-200 dark:bg-emerald-500/25 rounded-sm border border-emerald-300 dark:border-emerald-500/20 ml-0.5" />
                  </div>
                  {/* Data rows */}
                  <div className="flex-1 p-1.5 space-y-1">
                    {/* Good row */}
                    <div className="flex gap-px">
                      <div className="flex-1 h-1.5 bg-gray-300 dark:bg-gray-600/30 rounded-sm" />
                      <div className="flex-1 h-1.5 bg-gray-300 dark:bg-gray-600/30 rounded-sm" />
                      <div className="flex-1 h-1.5 bg-red-100 dark:bg-red-400/10 rounded-sm" />
                      <div className="flex-1 h-1.5 bg-gray-300 dark:bg-gray-600/30 rounded-sm" />
                    </div>
                    {/* Empty row — struck through */}
                    <div className="flex gap-px relative opacity-40">
                      <div className="flex-1 h-1.5 bg-gray-200 dark:bg-gray-700/20 rounded-sm" />
                      <div className="flex-1 h-1.5 bg-gray-200 dark:bg-gray-700/20 rounded-sm" />
                      <div className="flex-1 h-1.5 bg-gray-200 dark:bg-gray-700/20 rounded-sm" />
                      <div className="flex-1 h-1.5 bg-gray-200 dark:bg-gray-700/20 rounded-sm" />
                      <div className="absolute inset-y-0 left-0 right-0 flex items-center">
                        <div className="w-full h-px bg-red-400/50" />
                      </div>
                    </div>
                    {/* Good row */}
                    <div className="flex gap-px">
                      <div className="flex-1 h-1.5 bg-gray-300 dark:bg-gray-600/30 rounded-sm" />
                      <div className="flex-1 h-1.5 bg-gray-300 dark:bg-gray-600/30 rounded-sm" />
                      <div className="flex-1 h-1.5 bg-red-100 dark:bg-red-400/10 rounded-sm" />
                      <div className="flex-1 h-1.5 bg-gray-300 dark:bg-gray-600/30 rounded-sm" />
                    </div>
                    {/* Good row */}
                    <div className="flex gap-px">
                      <div className="flex-1 h-1.5 bg-gray-300 dark:bg-gray-600/30 rounded-sm" />
                      <div className="flex-1 h-1.5 bg-gray-300 dark:bg-gray-600/30 rounded-sm" />
                      <div className="flex-1 h-1.5 bg-red-100 dark:bg-red-400/10 rounded-sm" />
                      <div className="flex-1 h-1.5 bg-gray-300 dark:bg-gray-600/30 rounded-sm" />
                    </div>
                    {/* Empty row — struck through */}
                    <div className="flex gap-px relative opacity-40">
                      <div className="flex-1 h-1.5 bg-gray-200 dark:bg-gray-700/20 rounded-sm" />
                      <div className="flex-1 h-1.5 bg-gray-200 dark:bg-gray-700/20 rounded-sm" />
                      <div className="flex-1 h-1.5 bg-gray-200 dark:bg-gray-700/20 rounded-sm" />
                      <div className="flex-1 h-1.5 bg-gray-200 dark:bg-gray-700/20 rounded-sm" />
                      <div className="absolute inset-y-0 left-0 right-0 flex items-center">
                        <div className="w-full h-px bg-red-400/50" />
                      </div>
                    </div>
                    {/* Good row */}
                    <div className="flex gap-px">
                      <div className="flex-1 h-1.5 bg-gray-300 dark:bg-gray-600/30 rounded-sm" />
                      <div className="flex-1 h-1.5 bg-gray-300 dark:bg-gray-600/30 rounded-sm" />
                      <div className="flex-1 h-1.5 bg-red-100 dark:bg-red-400/10 rounded-sm" />
                      <div className="flex-1 h-1.5 bg-gray-300 dark:bg-gray-600/30 rounded-sm" />
                    </div>
                  </div>

                  {/* Subtle shimmer */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-emerald-400/5 to-transparent animate-pulse" />
                </div>

                {/* Sparkle badge */}
                <div className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-emerald-500/90 border-2 border-white dark:border-gray-900 flex items-center justify-center shadow-md">
                  <FaFilter className="w-2 h-2 text-white" />
                </div>
              </div>
            </div>

            {/* Description */}
            <div className="text-center px-2">
              <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-1">Auto-Clean Your Data</h3>
              <p className="text-xs text-gray-500 leading-relaxed">
                Automatically remove empty rows, duplicate columns, and sparse data so your extracted results are clean and ready to use.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Basic Cleaning Options */}
      <div className="bg-white dark:bg-gray-900/20 border border-gray-200 dark:border-gray-700/30 rounded-2xl p-4 shadow-sm">
        <div className="flex items-center mb-3 pb-2 border-b border-gray-200 dark:border-gray-700/30">
          <div className="p-1.5 rounded-lg bg-emerald-100 dark:bg-emerald-500/15 mr-2">
            <FaFilter className="text-emerald-500 dark:text-emerald-400 w-3 h-3" />
          </div>
          <h3 className="text-xs font-semibold text-gray-600 dark:text-gray-200 uppercase tracking-wide">Basic Cleaning</h3>
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between py-1.5 px-2 rounded-lg hover:bg-gray-50 dark:hover:bg-white/[0.02] transition-colors">
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Remove Empty Rows</label>
              <p className="text-2xs text-gray-400 dark:text-gray-500">Remove rows where all values are empty</p>
            </div>
            <Toggle enabled={config.removeEmptyRows} onChange={() => handleToggle("removeEmptyRows")} />
          </div>

          <div className="flex items-center justify-between py-1.5 px-2 rounded-lg hover:bg-gray-50 dark:hover:bg-white/[0.02] transition-colors">
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Remove Empty Columns</label>
              <p className="text-2xs text-gray-400 dark:text-gray-500">Remove columns where all values are empty</p>
            </div>
            <Toggle enabled={config.removeEmptyColumns} onChange={() => handleToggle("removeEmptyColumns")} />
          </div>

          <div className="flex items-center justify-between py-1.5 px-2 rounded-lg hover:bg-gray-50 dark:hover:bg-white/[0.02] transition-colors">
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Remove Duplicate Columns</label>
              <p className="text-2xs text-gray-400 dark:text-gray-500">Remove columns with identical data signatures</p>
            </div>
            <Toggle enabled={config.removeDuplicateColumns} onChange={() => handleToggle("removeDuplicateColumns")} />
          </div>
        </div>
      </div>

      {/* Advanced Cleaning Options */}
      <div className="bg-white dark:bg-gray-900/20 border border-gray-200 dark:border-gray-700/30 rounded-2xl p-4 shadow-sm">
        <div className="flex items-center mb-3 pb-2 border-b border-gray-200 dark:border-gray-700/30">
          <div className="p-1.5 rounded-lg bg-amber-100 dark:bg-amber-500/15 mr-2">
            <FaCog className="text-amber-500 dark:text-amber-400 w-3 h-3" />
          </div>
          <h3 className="text-xs font-semibold text-gray-600 dark:text-gray-200 uppercase tracking-wide">Advanced Cleaning</h3>
        </div>

        <div className="space-y-2">
          {/* Sparse Columns */}
          <div className="flex items-center justify-between py-1.5 px-2 rounded-lg hover:bg-gray-50 dark:hover:bg-white/[0.02] transition-colors">
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Remove Sparse Columns</label>
              <p className="text-2xs text-gray-400 dark:text-gray-500">Remove columns with too many empty values</p>
            </div>
            <Toggle enabled={config.removeSparseColumns} onChange={() => handleToggle("removeSparseColumns")} />
          </div>

          {config.removeSparseColumns && (
            <div className="space-y-2.5 p-3 mx-1 rounded-xl bg-amber-50 dark:bg-transparent dark:bg-gradient-to-br dark:from-amber-500/10 dark:to-orange-500/5 border border-amber-200 dark:border-amber-500/20">
              <div className="flex items-center justify-between">
                <label className="text-xs font-medium text-gray-500 dark:text-gray-400">Empty Threshold</label>
                <span className="text-xs font-semibold text-amber-600 dark:text-amber-300 bg-amber-100 dark:bg-amber-500/15 px-2 py-0.5 rounded-md tabular-nums">
                  {(config.sparseThreshold * 100).toFixed(0)}%
                </span>
              </div>
              <input
                type="range"
                min="0"
                max="1"
                step="0.05"
                value={config.sparseThreshold}
                onChange={(e) => handleThresholdChange("sparseThreshold", e.target.value)}
                className="w-full h-1.5 bg-gray-200 dark:bg-gray-700/60 rounded-full appearance-none cursor-pointer slider-orange"
              />
              <p className="text-2xs text-gray-400 dark:text-gray-500">
                Columns with ≥{(config.sparseThreshold * 100).toFixed(0)}% empty values will be removed
              </p>
            </div>
          )}

          {/* Repeating Columns */}
          <div className="flex items-center justify-between py-1.5 px-2 rounded-lg hover:bg-gray-50 dark:hover:bg-white/[0.02] transition-colors">
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Remove Repeating Columns</label>
              <p className="text-2xs text-gray-400 dark:text-gray-500">Remove columns where most values are identical</p>
            </div>
            <Toggle enabled={config.removeRepeatingColumns} onChange={() => handleToggle("removeRepeatingColumns")} />
          </div>

          {config.removeRepeatingColumns && (
            <div className="space-y-2.5 p-3 mx-1 rounded-xl bg-violet-50 dark:bg-transparent dark:bg-gradient-to-br dark:from-violet-500/10 dark:to-purple-500/5 border border-violet-200 dark:border-violet-500/20">
              <div className="flex items-center justify-between">
                <label className="text-xs font-medium text-gray-500 dark:text-gray-400">Repetition Threshold</label>
                <span className="text-xs font-semibold text-violet-600 dark:text-violet-300 bg-violet-100 dark:bg-violet-500/15 px-2 py-0.5 rounded-md tabular-nums">
                  {(config.repeatingThreshold * 100).toFixed(0)}%
                </span>
              </div>
              <input
                type="range"
                min="0"
                max="1"
                step="0.05"
                value={config.repeatingThreshold}
                onChange={(e) => handleThresholdChange("repeatingThreshold", e.target.value)}
                className="w-full h-1.5 bg-gray-200 dark:bg-gray-700/60 rounded-full appearance-none cursor-pointer slider-purple"
              />
              <p className="text-2xs text-gray-400 dark:text-gray-500">
                Columns with ≥{(config.repeatingThreshold * 100).toFixed(0)}% identical values will be removed
              </p>
            </div>
          )}

          {/* Sort by Density */}
          <div className="flex items-center justify-between py-1.5 px-2 rounded-lg hover:bg-gray-50 dark:hover:bg-white/[0.02] transition-colors">
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Sort Columns by Density</label>
              <p className="text-2xs text-gray-400 dark:text-gray-500">Reorder columns to show most filled data first</p>
            </div>
            <Toggle enabled={config.sortColumnsByDensity} onChange={() => handleToggle("sortColumnsByDensity")} />
          </div>
        </div>
      </div>

      <style jsx>{`
        .slider-orange::-webkit-slider-thumb {
          appearance: none;
          height: 14px;
          width: 14px;
          border-radius: 50%;
          background: linear-gradient(135deg, #fbbf24, #f59e0b);
          cursor: pointer;
          box-shadow: 0 1px 4px rgba(251, 191, 36, 0.3), 0 0 0 2px rgba(251, 191, 36, 0.1);
          border: none;
          transition: transform 0.15s ease, box-shadow 0.15s ease;
        }
        .slider-orange::-webkit-slider-thumb:hover {
          transform: scale(1.1);
          box-shadow: 0 2px 6px rgba(251, 191, 36, 0.4), 0 0 0 3px rgba(251, 191, 36, 0.15);
        }
        .slider-purple::-webkit-slider-thumb {
          appearance: none;
          height: 14px;
          width: 14px;
          border-radius: 50%;
          background: linear-gradient(135deg, #a78bfa, #8b5cf6);
          cursor: pointer;
          box-shadow: 0 1px 4px rgba(167, 139, 250, 0.3), 0 0 0 2px rgba(167, 139, 250, 0.1);
          border: none;
          transition: transform 0.15s ease, box-shadow 0.15s ease;
        }
        .slider-purple::-webkit-slider-thumb:hover {
          transform: scale(1.1);
          box-shadow: 0 2px 6px rgba(167, 139, 250, 0.4), 0 0 0 3px rgba(167, 139, 250, 0.15);
        }
      `}</style>
    </div>
  );
};

export default DataCleaningConfig;
